<template>
  <div class="page">
    <header class="top">
      <router-link to="/" class="wordmark">WHY Technologies</router-link>
      <div class="topnav" role="navigation" aria-label="Primary">
        <router-link to="/products">Work</router-link>
        <router-link to="/about" class="current">About</router-link>
      </div>
    </header>

    <main class="content">
      <section class="intro">
        <h1>WHY Technologies is a software company based in Bethesda, Maryland.</h1>
        <p>
          We build and operate custom software and workflow automation for
          professional-services firms, with a focus on legal operations.
          Founded in 2024.
        </p>
      </section>

      <section class="block">
        <h2>What we do</h2>
        <p>
          We work as the technology team for the businesses we serve. That means
          building the platform they run on, operating it day to day, and
          extending it as the business grows, rather than handing over a project
          and leaving.
        </p>
        <p>
          The work covers client management systems, client and partner portals,
          document handling, communications, and the automation of routine
          operational tasks. Everything is built around how the business
          actually runs, and every automated action is logged and reviewable by
          staff.
        </p>
      </section>

      <section class="block">
        <h2>Current work</h2>
        <p>
          We build and operate the operations platform for a multi-firm legal
          services group in Texas. It handles case and client management,
          document intake, telephony and payments, and the portals their clients
          and referral partners use.
        </p>
        <p>
          We are extending that platform with an assisted-workflow layer that
          prepares documents and handles routine follow-up under staff review.
        </p>
      </section>

      <section class="block">
        <h2>Leadership</h2>
        <p class="person">
          <span class="name">Saeshan Selva</span>
          <span class="role">Founder and CEO</span>
          <a href="mailto:sae@whyt.io">sae@whyt.io</a>
        </p>
        <p class="note">
          WHY Technologies is a member of Microsoft for Startups.
        </p>
      </section>
    </main>

    <footer class="footer">
      <span>WHY Technologies Inc.</span>
      <span>Bethesda, Maryland</span>
    </footer>
  </div>
</template>

<script>
export default {
  name: "AboutView",
};
</script>

<style scoped>
.page {
  --ink: #1e2126;
  --muted: #5c6066;
  --rule: #e6e7e9;
  --accent: #1f3a5f;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: var(--ink);
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.top {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 32px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--rule);
}

.wordmark {
  color: var(--ink);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: -0.01em;
}

.wordmark:hover,
.wordmark:focus-visible {
  color: var(--accent);
  outline: none;
}

.topnav {
  display: flex;
  align-items: center;
  gap: 28px;
  width: auto;
  position: static;
}

.topnav a {
  color: var(--muted);
  text-decoration: none;
  font-size: 0.95rem;
  padding-bottom: 3px;
  border-bottom: 1px solid transparent;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.topnav a:hover,
.topnav a:focus-visible {
  color: var(--ink);
  outline: none;
}

.topnav a.current {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.content {
  flex: 1;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  padding: 80px 24px 96px;
}

.intro h1 {
  font-size: clamp(1.75rem, 3.2vw, 2.5rem);
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 24px;
}

.intro p {
  font-size: 1.125rem;
  line-height: 1.65;
  color: var(--muted);
  margin: 0;
}

.block {
  margin-top: 64px;
  padding-top: 32px;
  border-top: 1px solid var(--rule);
}

.block h2 {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--muted);
  margin: 0 0 16px;
}

.block p {
  font-size: 1.0625rem;
  line-height: 1.7;
  margin: 0 0 16px;
}

.block p:last-child {
  margin-bottom: 0;
}

.person {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.name {
  font-weight: 500;
}

.role {
  color: var(--muted);
}

.person a,
.footer a {
  color: var(--accent);
  text-decoration: none;
}

.person a:hover,
.person a:focus-visible,
.footer a:hover,
.footer a:focus-visible {
  text-decoration: underline;
  outline: none;
}

.note {
  margin-top: 24px;
  color: var(--muted);
  font-size: 0.95rem;
}

.footer {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 28px 24px;
  border-top: 1px solid var(--rule);
  font-size: 0.875rem;
  color: var(--muted);
}

@media (max-width: 600px) {
  .top {
    padding: 16px 20px;
  }
  .topnav {
    gap: 20px;
  }
  .content {
    padding: 56px 20px 72px;
  }
  .block {
    margin-top: 48px;
  }
  .footer {
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
}
</style>